import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { deletionRequestsAPI } from "../api";
import type { DeletionRequest, DeletionRequestStats } from "../api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trash2,
  Eye,
  Check,
  X,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

export function DeletionRequestsPage() {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<DeletionRequest | null>(null);
  const [actionType, setActionType] = useState<"approve" | "reject" | "complete" | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const queryClient = useQueryClient();

  const pageLimit = 20;

  // Debounce search input and reset pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset pagination when status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  // Fetch deletion requests
  const { data: requestsData, isLoading: requestsLoading, error: requestsError } = useQuery({
    queryKey: ['deletionRequests', searchTerm, statusFilter, currentPage],
    queryFn: () => deletionRequestsAPI.getDeletionRequests({
      search: searchTerm || undefined,
      status: statusFilter,
      page: currentPage,
      limit: pageLimit,
    }),
  });

  // Fetch statistics
  const { data: statsData } = useQuery({
    queryKey: ['deletionRequestStats'],
    queryFn: () => deletionRequestsAPI.getStats(),
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: ({ requestId, notes }: { requestId: string; notes?: string }) =>
      deletionRequestsAPI.approve(requestId, notes),
    onSuccess: () => {
      toast.success("Deletion request approved");
      queryClient.invalidateQueries({ queryKey: ['deletionRequests'] });
      queryClient.invalidateQueries({ queryKey: ['deletionRequestStats'] });
      handleCloseActionDialog();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || "Failed to approve request");
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ requestId, notes }: { requestId: string; notes: string }) =>
      deletionRequestsAPI.reject(requestId, notes),
    onSuccess: () => {
      toast.success("Deletion request rejected");
      queryClient.invalidateQueries({ queryKey: ['deletionRequests'] });
      queryClient.invalidateQueries({ queryKey: ['deletionRequestStats'] });
      handleCloseActionDialog();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || "Failed to reject request");
    },
  });

  // Complete mutation
  const completeMutation = useMutation({
    mutationFn: ({ requestId, notes }: { requestId: string; notes?: string }) =>
      deletionRequestsAPI.complete(requestId, notes),
    onSuccess: () => {
      toast.success("User account deleted successfully");
      queryClient.invalidateQueries({ queryKey: ['deletionRequests'] });
      queryClient.invalidateQueries({ queryKey: ['deletionRequestStats'] });
      handleCloseActionDialog();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || "Failed to complete request");
    },
  });

  const requests = requestsData?.requests || [];
  const pagination = requestsData?.pagination || { page: 1, limit: pageLimit, total: 0, pages: 0 };
  const stats: DeletionRequestStats = statsData || { pending: 0, approved: 0, rejected: 0, completed: 0, cancelled: 0, total: 0 };

  const handleViewDetails = (request: DeletionRequest) => {
    setSelectedRequest(request);
    setDetailsDialogOpen(true);
  };

  const handleAction = (request: DeletionRequest, action: "approve" | "reject" | "complete") => {
    setSelectedRequest(request);
    setActionType(action);
    setResolutionNotes("");
    setActionDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedRequest(null);
  };

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setSelectedRequest(null);
    setActionType(null);
    setResolutionNotes("");
  };

  const handleConfirmAction = () => {
    if (!selectedRequest || !actionType) return;

    if (actionType === "reject" && !resolutionNotes.trim()) {
      toast.error("Resolution notes are required when rejecting a request");
      return;
    }

    if (actionType === "approve") {
      approveMutation.mutate({ requestId: selectedRequest._id, notes: resolutionNotes || undefined });
    } else if (actionType === "reject") {
      rejectMutation.mutate({ requestId: selectedRequest._id, notes: resolutionNotes });
    } else if (actionType === "complete") {
      completeMutation.mutate({ requestId: selectedRequest._id, notes: resolutionNotes || undefined });
    }
  };

  const getUserFromRequest = (request: DeletionRequest) => {
    if (typeof request.user_id === 'object') {
      return request.user_id;
    }
    return null;
  };

  const getUserInitials = (firstName?: string, lastName?: string) => {
    const f = firstName?.charAt(0) || '';
    const l = lastName?.charAt(0) || '';
    return (f + l).toUpperCase() || '?';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-600"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'approved':
        // "Approved" on its own read as finished, and the account carried on
        // signing in. Approval only records the decision — the Delete button
        // beside it is what actually removes anything.
        return <Badge variant="outline" className="text-blue-600 border-blue-600"><CheckCircle className="h-3 w-3 mr-1" />Approved — not deleted yet</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="text-red-600 border-red-600"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'completed':
        return <Badge variant="default" className="bg-green-600"><Check className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'cancelled':
        return <Badge variant="secondary"><X className="h-3 w-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const isActionPending = approveMutation.isPending || rejectMutation.isPending || completeMutation.isPending;

  if (requestsLoading) {
    return (
      <DashboardLayout>
        <LoadingState message="Loading deletion requests..." />
      </DashboardLayout>
    );
  }

  if (requestsError) {
    return (
      <DashboardLayout>
        <ErrorState message="Failed to load deletion requests" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Account Deletion Requests"
          description="Review and manage user account deletion requests"
        />

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pending}</div>
              <p className="text-xs text-muted-foreground">Awaiting review</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Approved</CardTitle>
              <CheckCircle className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.approved}</div>
              <p className="text-xs text-muted-foreground">Ready to complete</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <Trash2 className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.completed}</div>
              <p className="text-xs text-muted-foreground">Accounts deleted</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rejected</CardTitle>
              <XCircle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.rejected}</div>
              <p className="text-xs text-muted-foreground">Request denied</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Cancelled</CardTitle>
              <RefreshCw className="h-4 w-4 text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.cancelled}</div>
              <p className="text-xs text-muted-foreground">By user</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search & Filter
            </CardTitle>
            <CardDescription>Filter deletion requests by status or search by user</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Search by name or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchInput("");
                  setStatusFilter("all");
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table */}
        <Card>
          <CardHeader>
            <CardTitle>Deletion Requests</CardTitle>
            <CardDescription>
              Showing {requests.length} of {pagination.total} requests
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Reviewed By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No deletion requests found
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests.map((request) => {
                      const user = getUserFromRequest(request);
                      const snapshot = request.user_snapshot;
                      return (
                        <TableRow key={request._id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar>
                                <AvatarImage src={user?.avatar_url} />
                                <AvatarFallback>
                                  {getUserInitials(snapshot.first_name, snapshot.last_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">
                                  {snapshot.first_name} {snapshot.last_name}
                                </div>
                                <div className="text-xs text-muted-foreground">{snapshot.email}</div>
                                {user?.is_delete && (
                                  <Badge variant="destructive" className="text-xs mt-1">Deleted</Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {typeof snapshot.school_id === 'object' ? snapshot.school_id?.school_name : '-'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {typeof snapshot.campus_id === 'object' ? snapshot.campus_id?.campus_name : '-'}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <div className="truncate" title={request.reason}>
                              {request.reason}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(request.status)}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {new Date(request.createdAt).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(request.createdAt).toLocaleTimeString()}
                            </div>
                          </TableCell>
                          <TableCell>
                            {request.reviewed_by ? (
                              <div className="text-sm">
                                {typeof request.reviewed_by === 'object' ? (
                                  <>
                                    {request.reviewed_by.first_name} {request.reviewed_by.last_name}
                                    <div className="text-xs text-muted-foreground">
                                      {request.reviewed_at && new Date(request.reviewed_at).toLocaleDateString()}
                                    </div>
                                  </>
                                ) : '-'}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewDetails(request)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {request.status === 'pending' && (
                                <>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handleAction(request, "approve")}
                                    className="bg-blue-600 hover:bg-blue-700"
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleAction(request, "reject")}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              {request.status === 'approved' && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleAction(request, "complete")}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.pages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                    disabled={currentPage === pagination.pages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={(open) => !open && handleCloseDetailsDialog()}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Deletion Request Details</DialogTitle>
              <DialogDescription>
                Request from {selectedRequest?.user_snapshot.first_name} {selectedRequest?.user_snapshot.last_name}
              </DialogDescription>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-6">
                {/* User Info */}
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={getUserFromRequest(selectedRequest)?.avatar_url} />
                      <AvatarFallback className="text-lg">
                        {getUserInitials(selectedRequest.user_snapshot.first_name, selectedRequest.user_snapshot.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="text-lg font-medium">
                        {selectedRequest.user_snapshot.first_name} {selectedRequest.user_snapshot.last_name}
                      </div>
                      <div className="text-sm text-muted-foreground">{selectedRequest.user_snapshot.email}</div>
                      <div className="text-sm text-muted-foreground">
                        Role: {selectedRequest.user_snapshot.role}
                      </div>
                      {getUserFromRequest(selectedRequest)?.is_delete && (
                        <Badge variant="destructive" className="mt-2">Account Deleted</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Request Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Requested At</Label>
                    <div className="text-sm mt-1">{new Date(selectedRequest.createdAt).toLocaleString()}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">School</Label>
                    <div className="text-sm mt-1">
                      {typeof selectedRequest.user_snapshot.school_id === 'object'
                        ? selectedRequest.user_snapshot.school_id?.school_name
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Campus</Label>
                    <div className="text-sm mt-1">
                      {typeof selectedRequest.user_snapshot.campus_id === 'object'
                        ? selectedRequest.user_snapshot.campus_id?.campus_name
                        : '-'}
                    </div>
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <Label className="text-xs text-muted-foreground">Reason for Deletion</Label>
                  <div className="p-3 bg-muted rounded-lg mt-1">
                    <p className="text-sm">{selectedRequest.reason}</p>
                  </div>
                </div>

                {/* Review Info */}
                {selectedRequest.reviewed_by && (
                  <div className="border-t pt-4">
                    <Label className="text-xs text-muted-foreground">Review Information</Label>
                    <div className="mt-2 space-y-2">
                      <div className="text-sm">
                        <span className="font-medium">Reviewed by:</span>{' '}
                        {typeof selectedRequest.reviewed_by === 'object'
                          ? `${selectedRequest.reviewed_by.first_name} ${selectedRequest.reviewed_by.last_name}`
                          : '-'}
                      </div>
                      {selectedRequest.reviewed_at && (
                        <div className="text-sm">
                          <span className="font-medium">Reviewed at:</span>{' '}
                          {new Date(selectedRequest.reviewed_at).toLocaleString()}
                        </div>
                      )}
                      {selectedRequest.resolution_notes && (
                        <div>
                          <span className="font-medium text-sm">Notes:</span>
                          <p className="text-sm mt-1 p-2 bg-muted rounded">{selectedRequest.resolution_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cancellation Info */}
                {selectedRequest.status === 'cancelled' && selectedRequest.cancelled_at && (
                  <div className="border-t pt-4">
                    <Label className="text-xs text-muted-foreground">Cancellation Information</Label>
                    <div className="mt-2 space-y-2">
                      <div className="text-sm">
                        <span className="font-medium">Cancelled at:</span>{' '}
                        {new Date(selectedRequest.cancelled_at).toLocaleString()}
                      </div>
                      {selectedRequest.cancellation_reason && (
                        <div className="text-sm">
                          <span className="font-medium">Reason:</span> {selectedRequest.cancellation_reason}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Completion Info */}
                {selectedRequest.status === 'completed' && selectedRequest.completed_at && (
                  <div className="border-t pt-4">
                    <Label className="text-xs text-muted-foreground">Completion Information</Label>
                    <div className="mt-2">
                      <div className="text-sm">
                        <span className="font-medium">Completed at:</span>{' '}
                        {new Date(selectedRequest.completed_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDetailsDialog}>
                Close
              </Button>
              {selectedRequest?.status === 'pending' && (
                <>
                  <Button
                    variant="default"
                    onClick={() => {
                      handleCloseDetailsDialog();
                      handleAction(selectedRequest, "approve");
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      handleCloseDetailsDialog();
                      handleAction(selectedRequest, "reject");
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </>
              )}
              {selectedRequest?.status === 'approved' && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    handleCloseDetailsDialog();
                    handleAction(selectedRequest, "complete");
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Account
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Action Dialog */}
        <Dialog open={actionDialogOpen} onOpenChange={(open) => !open && handleCloseActionDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === 'approve' && 'Approve Deletion Request'}
                {actionType === 'reject' && 'Reject Deletion Request'}
                {actionType === 'complete' && 'Complete Deletion Request'}
              </DialogTitle>
              <DialogDescription>
                {actionType === 'approve' && 'This will mark the request as approved. You can then complete the deletion later.'}
                {actionType === 'reject' && 'This will reject the deletion request. The user will not be deleted.'}
                {actionType === 'complete' && (
                  <span className="text-destructive font-medium">
                    Warning: This will permanently delete the user account. This action cannot be undone.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={getUserFromRequest(selectedRequest)?.avatar_url} />
                      <AvatarFallback>
                        {getUserInitials(selectedRequest.user_snapshot.first_name, selectedRequest.user_snapshot.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">
                        {selectedRequest.user_snapshot.first_name} {selectedRequest.user_snapshot.last_name}
                      </div>
                      <div className="text-sm text-muted-foreground">{selectedRequest.user_snapshot.email}</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>
                    Resolution Notes {actionType === 'reject' && <span className="text-destructive">*</span>}
                  </Label>
                  <Textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder={
                      actionType === 'reject'
                        ? "Explain why this request is being rejected (required)..."
                        : "Add any notes about this action (optional)..."
                    }
                    maxLength={2000}
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">{resolutionNotes.length}/2000 characters</p>
                </div>

                {actionType === 'complete' && (
                  <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-destructive">This action is irreversible</p>
                        <p className="text-muted-foreground mt-1">
                          The user account will be soft-deleted and they will no longer be able to access the app.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseActionDialog} disabled={isActionPending}>
                Cancel
              </Button>
              <Button
                variant={actionType === 'approve' ? 'default' : 'destructive'}
                onClick={handleConfirmAction}
                disabled={isActionPending || (actionType === 'reject' && !resolutionNotes.trim())}
                className={actionType === 'approve' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                {isActionPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {actionType === 'approve' && <><Check className="h-4 w-4 mr-1" />Approve</>}
                    {actionType === 'reject' && <><X className="h-4 w-4 mr-1" />Reject</>}
                    {actionType === 'complete' && <><Trash2 className="h-4 w-4 mr-1" />Delete Account</>}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
